import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"

import { getM365Connection } from "@/api/client/m365-connection"
import { m365ConnectionHref } from "@/features/auth/utils/m365-connection"
import { cn } from "@/lib/utils"

import { m365PillState } from "../utils/agent-m365-pill-utils"
import { AGENT_STATUS_DOT_CLASS } from "../utils/agents-utils"

type AgentM365PillProps = {
  className?: string
  /**
   * The workspace renders this pill twice — once in the header (`sm` and up)
   * and once in the thread sheet (below `sm`) — and both copies stay in the
   * DOM, only one of them painted. Two nodes under one `data-testid` make
   * `getByTestId` ambiguous and a Playwright locator strict-mode fail, so each
   * placement names itself: the header keeps the canonical
   * `agent-m365-pill`, the sheet is `agent-m365-pill-sheet`. Same contract
   * slice A gave `agent-audience`, which stays a single node by living only in
   * the header.
   */
  testId?: string
}

/**
 * Microsoft 365 connection as a header status pill (it used to be a loose
 * button in the rail). The dot never claims a connection that has not been
 * read back: anything but a confirmed `connected` is neutral or attention,
 * and the exact status is on the pill's accessible name. A 503 (the M365
 * service itself is down) gets a visible "Unavailable" state, and the link
 * carries the current thread so the connection page's Return comes back here.
 */
export function AgentM365Pill({ className, testId = "agent-m365-pill" }: AgentM365PillProps) {
  const { t } = useTranslation()
  // Same query key as `M365ConnectionCard`, so the pill and the connection
  // page share one cached status instead of polling twice.
  const connection = useQuery({
    queryKey: ["m365-connection"],
    queryFn: getM365Connection,
    retry: false,
    staleTime: 60_000,
  })
  const location = useLocation()
  const state = m365PillState(connection)
  const serviceDown = state.kind === "service_unavailable"
  let status = t("agents.connection.loading", { defaultValue: "Checking connection…" })
  if (serviceDown)
    status = t("agents.connection.service_unavailable", {
      defaultValue: "Microsoft 365 is unavailable on this deployment right now.",
    })
  else if (state.kind === "unknown")
    status = t("agents.connection.unavailable", {
      defaultValue: "Connection status is unavailable.",
    })
  else if (state.kind === "status") status = t(`agents.connection.status.${state.status}`)

  return (
    <Link
      data-state={state.kind}
      data-testid={testId}
      data-tone={state.tone}
      title={`${t("agents.connection.title", { defaultValue: "Microsoft 365 connection" })}: ${status}`}
      to={m365ConnectionHref(`${location.pathname}${location.search}${location.hash}`)}
      className={cn(
        "border-border bg-card text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none",
        className
      )}
    >
      {/* Status dot: rounded-full is reserved for avatars/status dots (DESIGN.md). */}
      <span
        aria-hidden="true"
        className={cn("size-2 shrink-0 rounded-full", AGENT_STATUS_DOT_CLASS[state.tone])}
      />
      {t("agents.connection.short", { defaultValue: "Microsoft 365" })}
      {serviceDown ? (
        <span aria-hidden="true" className="text-foreground">
          {`· ${t("agents.connection.short_unavailable", { defaultValue: "Unavailable" })}`}
        </span>
      ) : null}
      <span className="sr-only">{`: ${status}`}</span>
    </Link>
  )
}
